import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { certifications } from '../data/mock';
import { Button } from '../components/ui/button';
import { ShieldCheck, Bug, Award, FileText, Scale, Leaf, ArrowRight, CheckCircle2 } from 'lucide-react';
import { SEO } from '../components/SEO';

const iconMap = {
  ShieldCheck: ShieldCheck,
  Bug: Bug,
  Award: Award,
  FileText: FileText,
  Scale: Scale,
  Leaf: Leaf,
};

export default function CompliancePage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Trade Compliance"
        path="/compliance"
        description="Trade compliance and documentation services ensuring your imports and exports meet Australian and international regulatory requirements."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-navy-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-medium mb-6">
                Trust & Compliance
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Certifications &
                <span className="block text-teal-400">Compliance Standards</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                We maintain the highest standards of compliance with Australian import and export regulations 
                and international trade requirements.
              </p>
            </div>
          </div>
        </section>

        {/* Certifications Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {certifications.map((cert) => {
                const Icon = iconMap[cert.icon];
                return (
                  <div
                    key={cert.title}
                    className="p-8 rounded-2xl bg-slate-50 border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="p-4 w-fit rounded-xl bg-navy-900 mb-6">
                      <Icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-navy-900 mb-3">{cert.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{cert.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Compliance Process */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-navy-900 mb-4">Our Compliance Process</h2>
              <p className="text-xl text-slate-600">A systematic approach to ensuring every export meets all requirements</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div className="space-y-6">
                {[
                  { title: 'Product Assessment', desc: 'Thorough evaluation of products against import/export regulations and destination requirements' },
                  { title: 'Documentation Preparation', desc: 'Complete preparation of all required trade certificates and customs documentation' },
                  { title: 'Quality Verification', desc: 'Pre-shipment inspection and quality assurance checks' },
                  { title: 'Compliance Review', desc: 'Final review of all compliance requirements before shipment' },
                  { title: 'Ongoing Monitoring', desc: 'Continuous monitoring of regulatory changes and requirements' },
                ].map((step, index) => (
                  <div key={step.title} className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-navy-900 text-white flex items-center justify-center font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-navy-900 mb-1">{step.title}</h3>
                      <p className="text-slate-600">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white p-8 rounded-2xl border border-slate-200">
                <h3 className="text-2xl font-bold text-navy-900 mb-6">Documentation We Handle</h3>
                <div className="space-y-4">
                  {[
                    'Commercial Invoice & Packing List',
                    'Certificate of Origin',
                    'Phytosanitary Certificate',
                    'Fumigation Certificate',
                    'Health Certificate',
                    'Bill of Lading / Airway Bill',
                    'Import & Export Permits',
                    'Customs Declaration Forms',
                    'Quality Inspection Reports',
                    'Insurance Certificates',
                  ].map((doc) => (
                    <div key={doc} className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-teal-600 flex-shrink-0" />
                      <span className="text-slate-700">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust CTA */}
        <section className="py-20 bg-navy-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <ShieldCheck className="h-16 w-16 text-teal-400 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Trade With Confidence
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Our commitment to compliance means you can focus on growing your business 
              while we handle the regulatory complexities.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                Discuss Your Requirements
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
